import {
    compareAsc,
    format as dateFnsFormat,
    isValid,
    parse,
    parseISO
} from 'date-fns'
import {formatInTimeZone} from 'date-fns-tz'

export const TZ = {
    LOCAL: 'local',
    UTC: 'UTC'
}

export const DateFormats = {
    SLASH_DMY: 'dd/MM/yyyy',
    SLASH_MDY: 'MM/dd/yyyy',
    DOT_DMY: 'dd.MM.yyyy',
    DOT_MDY: 'MM.dd.yyyy',
    DASH_YMD: 'yyyy-MM-dd',
    DASH_DMY: 'dd-MM-yyyy',

    SLASH_DMY_TIME: 'dd/MM/yyyy, HH:mm:ss',
    SLASH_DMY_TIME_SHORT: 'dd/MM/yyyy, HH:mm',
    DOT_DMY_TIME: 'dd.MM.yyyy HH:mm:ss',
    DASH_YMD_TIME: 'yyyy-MM-dd HH:mm:ss',
    DASH_YMD_TIME_SHORT: 'yyyy-MM-dd HH:mm',
    TIME_SLASH_DMY: 'HH:mm / dd.MM.yyyy',
    TIME_DATE_SHORT: 'HH:mm / dd MMM',

    ISO_DATE: 'yyyy-MM-dd',
    ISO_DATETIME: "yyyy-MM-dd'T'HH:mm:ss'Z'",
    ISO_DATETIME_ZERO_SEC: "yyyy-MM-dd'T'HH:mm:00'Z'",

    US_LONG: 'MMM dd, yyyy',
    EU_LONG: 'dd MMM yyyy',
    FULL_WEEKDAY: 'EEEE, MMMM dd, yyyy, HH:mm:ss',
    FULL_WEEKDAY_AT: "EEEE, dd MMMM yyyy 'at' HH:mm:ss",

    TIME_24: 'HH:mm:ss',
    TIME_24_SHORT: 'HH:mm',
    TIME_12: 'hh:mm a',

    MONTH_DAY: 'dd MMM',
    YEAR_MONTH: 'MMM yyyy',

    SLASH_DMY_TIME_UTC: "dd/MM/yyyy, HH:mm:ss 'UTC'"
}

export const normalize = (date, formatPattern = null) => {
    if (!date) return null

    if (date && typeof date.toDate === 'function') {
        const momentDate = date.toDate()
        return isValid(momentDate) ? momentDate : null
    }

    if (date instanceof Date) {
        return isValid(date) ? date : null
    }

    if (typeof date === 'string') {
        if (date.includes('T') && date.endsWith('Z')) {
            const parsed = parseISO(date)
            return isValid(parsed) ? parsed : null
        }

        if (formatPattern) {
            const parsed = parse(date.trim(), formatPattern, new Date())
            return isValid(parsed) ? parsed : null
        }

        const parsed = new Date(date)

        return isValid(parsed) ? parsed : null
    }

    return null
}

const parseTime = (timeString) => {
    if (!timeString || typeof timeString !== 'string') return null

    const [hours, minutes, seconds = 0] = timeString.split(':').map(Number)

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null

    return {hours, minutes, seconds}
}

const extractTimeObject = (time) => {
    if (time?.toDate && typeof time.toDate === 'function') {
        return time.toDate()
    }

    if (typeof time !== 'string') {
        return normalize(time)
    }

    const parsed = parseTime(time)
    if (!parsed) return normalize(time)

    const timeObj = new Date()
    timeObj.setHours(parsed.hours, parsed.minutes, parsed.seconds, 0)
    return timeObj
}

export const formatDate = (date, pattern, timezone = TZ.LOCAL) => {
    if (!date) return ''

    const d = normalize(date)
    if (!d) return ''

    if (timezone === TZ.LOCAL) {
        return dateFnsFormat(d, pattern)
    }

    return formatInTimeZone(d, timezone, pattern)
}

export const convertTimeStampToHourUTC = (date) => {
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    const seconds = String(date.getUTCSeconds()).padStart(2, '0')
    const milliseconds = String(date.getUTCMilliseconds()).padStart(3, '0')

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`
}

export const formatWithGMT = (date, pattern) => {
    if (!date) return ''

    const d = normalize(date)
    if (!d) return ''

    const formatted = dateFnsFormat(d, pattern)
    const offset = -d.getTimezoneOffset()
    const sign = offset >= 0 ? '+' : '-'
    const h = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')
    const m = String(Math.abs(offset) % 60).padStart(2, '0')

    return `${formatted} GMT${sign}${h}:${m}`
}

export const utcToLocal = (utcTime, showSeconds = false) => {
    const time = parseTime(utcTime)
    if (!time) return ''

    const date = new Date()
    date.setUTCHours(time.hours, time.minutes, time.seconds, 0)

    return dateFnsFormat(date, showSeconds ? DateFormats.TIME_24 : DateFormats.TIME_24_SHORT)
}

export const combine = (date, time, dateFormat = null, outputLocal = false) => {
    const dateObj = normalize(date, dateFormat)
    if (!dateObj) return null

    const timeObj = extractTimeObject(time)
    if (!timeObj) return null

    const combined = new Date(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate(),
        timeObj.getHours(),
        timeObj.getMinutes(),
        timeObj.getSeconds(),
        0
    )

    return outputLocal ? dateFnsFormat(combined, DateFormats.ISO_DATETIME) : combined.toISOString()
}

export const combineAndFormat = (dateSource, timeSource, pattern = DateFormats.SLASH_DMY_TIME) => {
    if (!dateSource || !timeSource) return ''

    const dateObj = normalize(dateSource, pattern)
    const timeObj = extractTimeObject(timeSource)

    if (!dateObj || !timeObj) return ''

    const combined = new Date(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate(),
        timeObj.getHours(),
        timeObj.getMinutes(),
        timeObj.getSeconds(),
        timeObj.getMilliseconds()
    )

    return dateFnsFormat(combined, pattern)
}

export const compare = (date1, date2, formatPattern = null) => {
    const d1 = normalize(date1, formatPattern)
    const d2 = normalize(date2, formatPattern)
    return d1 && d2 ? compareAsc(d1, d2) : null
}

export const isBefore = (d1, d2, fmt) => compare(d1, d2, fmt) < 0
export const isAfter = (d1, d2, fmt) => compare(d1, d2, fmt) > 0
export const isEqual = (d1, d2, fmt) => compare(d1, d2, fmt) === 0
export const isBeforeOrEqual = (d1, d2, fmt) => compare(d1, d2, fmt) <= 0
export const isAfterOrEqual = (d1, d2, fmt) => compare(d1, d2, fmt) >= 0

export const compareTime = (time1, time2) => {
    const t1 = parseTime(time1)
    const t2 = parseTime(time2)

    if (!t1 || !t2) return null

    const minutes1 = t1.hours * 60 + t1.minutes
    const minutes2 = t2.hours * 60 + t2.minutes

    return minutes1 - minutes2
}

export const isTimeBefore = (t1, t2) => compareTime(t1, t2) < 0
export const isTimeAfter = (t1, t2) => compareTime(t1, t2) > 0
export const isTimeEqual = (t1, t2) => compareTime(t1, t2) === 0
export const isTimeBeforeOrEqual = (d1, d2, fmt) => compareTime(d1, d2, fmt) <= 0
export const isTimeAfterOrEqual = (d1, d2, fmt) => compareTime(d1, d2, fmt) >= 0

export const formatTimeString = (time, seconds) => {
    if (!time) return ''
    const [hours, minutes] = time.split(':')
    const fixedHours = hours === '24' ? '00' : hours
    return seconds ? `${fixedHours}:${minutes}:${seconds}` : `${fixedHours}:${minutes}`
}

export const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

export const updateSeconds = (seconds, date, showMs = false) => {
    const val = parseFloat(seconds)

    if (isNaN(val) || val < 0 || val >= 60) {
        const d = normalize(date)
        return d ? d.toISOString() : ''
    }

    const d = normalize(date)
    if (!d) return ''

    const sec = Math.floor(val)
    const ms = showMs ? Math.round((val % 1) * 1000) : 0

    const updated = new Date(Date.UTC(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        d.getUTCHours(),
        d.getUTCMinutes(),
        sec,
        ms
    ))

    return showMs
        ? updated.toISOString()
        : updated.toISOString().replace('.000Z', 'Z')
}

export const addZero = num => {
    return num.toString().padStart(2, '0')
}

export const timeFormatter = (duration, unit = 'minutes') => {
    const MINUTES_IN = {
        seconds: 1 / 60,
        minutes: 1,
        hours: 60,
        days: 1440
    }

    const MINUTES_IN_MONTH = 43200
    const MINUTES_IN_WEEK = 10080
    const MINUTES_IN_DAY = 1440
    const MINUTES_IN_HOUR = 60

    const factor = MINUTES_IN[unit] ?? 1
    duration = Math.floor(duration * factor)

    const months = Math.floor(duration / MINUTES_IN_MONTH)
    duration %= MINUTES_IN_MONTH

    const weeks = Math.floor(duration / MINUTES_IN_WEEK)
    duration %= MINUTES_IN_WEEK

    const days = Math.floor(duration / MINUTES_IN_DAY)
    duration %= MINUTES_IN_DAY

    const hours = Math.floor(duration / MINUTES_IN_HOUR)
    const minutes = duration % MINUTES_IN_HOUR

    const parts = []
    if (months) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`)
    if (weeks) parts.push(`${weeks} ${weeks === 1 ? 'week' : 'weeks'}`)
    if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
    if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
    if (minutes) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)

    return {
        months,
        weeks,
        days,
        hours,
        minutes,
        monthText: months === 1 ? 'month' : 'months',
        weekText: weeks === 1 ? 'week' : 'weeks',
        dayText: days === 1 ? 'day' : 'days',
        hourText: hours === 1 ? 'hour' : 'hours',
        minuteText: minutes === 1 ? 'minute' : 'minutes',
        formatted: parts.join(' ')
    }
}

export const convertToSeconds = (value, unit, intervalUnits) => {
    const unitInfo = intervalUnits?.[unit]
    return value * (unitInfo?.interval || 1)
}

export const getValueFromTime = (time, index) => time.split(':')?.[index] || null
